<template>
  <div class="modal-mask">
    <div class="modal-container">
      <div class="seller-header">
        Números com Notificações Bloqueadas
        <span class="material-icons seller-header-close" v-on:click="close">cancel</span>
      </div>
      <div style="max-height: 95vh; overflow-y: auto; padding: 2em">
        <div class="search-filter-container">
          <div class="search-input-with-icon">
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Digite o número..."
              class="search-input"
              @keyup.enter="applyFilter"
            />
            <span class="material-icons search-icon">search</span>
          </div>
          <button @click="applyFilter" class="filter-button orange-button">
            Filtrar
          </button>
          <button
            @click="clearFilter"
            class="filter-button clear-button"
            :disabled="!appliedFilter"
          >
            Limpar
          </button>
        </div>

        <cc-loader v-if="loading" />

        <div v-if="!loading">
          <div class="blocked-numbers-table-header desktop">
            <div class="blocked-number-cell">Número</div>
            <div class="blocked-date-cell">Data do Bloqueio</div>
            <div class="blocked-actions-cell">Ações</div>
          </div>

          <div v-if="filteredNumbers.length === 0" class="no-blocked-numbers">
            {{
              appliedFilter
                ? `Nenhum número encontrado para: ${appliedFilter}`
                : "Nenhum número bloqueado cadastrado"
            }}
          </div>

          <div
            class="blocked-numbers-table-row"
            v-for="(number, idx) in paginatedNumbers"
            :key="number.id"
            :class="getSpecialBackground(idx)"
          >
            <div class="blocked-number-cell">
              {{ formatPhoneNumber(number.phone_number) }}
            </div>
            <div class="blocked-date-cell">
              {{ formatDate(number.blocked_at) }}
            </div>
            <div class="blocked-actions-cell">
              <span
                @click="showConfirmationModal(number)"
                class="material-icons-outlined seller-table-edit-icon"
                title="Restaurar notificações"
                >settings_backup_restore</span
              >
            </div>
          </div>
        </div>
        <div class="box-footer clearfix not-print">
          <cc-paginacao
            :key="'pag'"
            classes="orange"
            :items_by_page_value="itemsPerPage"
            :total="totalItems"
            :page="currentPage"
            :page_limit="11"
            @change="changePage"
          ></cc-paginacao>
        </div>
      </div>
    </div>
    <div v-if="showConfirmation" class="confirmation-modal-mask">
      <div class="confirmation-modal-container">
        <div class="confirmation-modal-header">
          <h3>Confirmar Ativação</h3>
          <span class="material-icons seller-header-close" @click="hideConfirmationModal"
            >close</span
          >
        </div>
        <div class="confirmation-modal-body">
          <p>
            Deseja ativar as notificações para o número
            <span class="products-page__datetime-subtitle">
              {{
                selectedNumber && selectedNumber.phone_number
                  ? formatPhoneNumber(selectedNumber.phone_number)
                  : ""
              }}</span
            >?
          </p>
        </div>
        <div class="confirmation-modal-footer">
          <button class="btn-cancel" @click="hideConfirmationModal">Cancelar</button>
          <button class="btn-confirm" @click="confirmRestore">Sim, Ativar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SellerService from "@/services/v3/providers/seller.service";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import Paginacao from "@/components/cliente/base-components/Pagination";

export default {
  mixins: [loaderMixin],
  data() {
    return {
      seller_svc: new SellerService(),
      loading: true,
      blockedNumbers: [],
      searchQuery: "",
      showConfirmation: false,
      appliedFilter: "",
      currentPage: 1,
      itemsPerPage: 20,
      totalItems: 0,
      selectedNumber: null,
    };
  },

  components: {
    ccPaginacao: Paginacao,
  },

  computed: {
    filteredNumbers() {
      if (!this.appliedFilter.trim()) return this.blockedNumbers;
      const searchTerm = this.normalizeForSearch(this.appliedFilter);
      return this.blockedNumbers.filter((number) => {
        const normalizedNumber = this.normalizeForSearch(number.phone_number);
        return normalizedNumber.includes(searchTerm);
      });
    },

    paginatedNumbers() {
      return this.filteredNumbers;
    },
  },

  methods: {
    close() {
      this.$emit("close");
    },
    handlekeydown(e) {
			if (e.key === "Escape") {
				this.close();
			}
    },
    getSpecialBackground(idx) {
      return idx % 2 !== 0 ? "page-table-line-special" : "";
    },
    formatPhoneNumber(number) {
      if (!number) return "";
      const cleaned = number.toString().replace(/\D/g, "");
      if (cleaned.length > 11) {
        return `+${cleaned.substring(0, 2)} (${cleaned.substring(
          2,
          4
        )}) ${cleaned.substring(4, 9)}-${cleaned.substring(9)}`;
      } else if (cleaned.length === 11) {
        return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
          2,
          7
        )}-${cleaned.substring(7)}`;
      } else if (cleaned.length === 10) {
        return `(${cleaned.substring(0, 2)}) ${cleaned.substring(
          2,
          6
        )}-${cleaned.substring(6)}`;
      }
      return number;
    },
    async loadBlockedNumbers() {
      this.loading = true;
      try {
        if (this.appliedFilter) {
          const searchTerm = this.normalizeForSearch(this.appliedFilter);
          if (searchTerm.length < 8) {
            this.$toast.warning("Digite pelo menos 8 dígitos para buscar");
            this.blockedNumbers = [];
            this.totalItems = 0;
            return;
          }
          await this.searchNumberAcrossAllPages(searchTerm);
        } else {
          const params = {
            page: this.currentPage,
            per_page: this.itemsPerPage,
          };

          const response = await Promise.race([
            this.seller_svc.getBlockedNumbers(params),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Tempo de busca excedido")), 60000)
            ),
          ]);

          if (!response || !response.data || !Array.isArray(response.data.data)) {
            throw new Error("Resposta inválida da API");
          }

          this.blockedNumbers = response.data.data.map((item) => ({
            id: item.id,
            phone_number: item.phone,
            blocked_at: item.created_at,
          }));
          this.totalItems = response.data.total || 0;
        }
      } catch (error) {
        let errorMessage = "Erro ao carregar números bloqueados";

        if (error.message.includes("timeout")) {
          errorMessage =
            "A busca está demorando muito. Tente novamente com critérios mais específicos.";
        } else if (error.response) {
          errorMessage =
            error.response.data?.message ||
            `Erro ${error.response.status}: Falha ao carregar números`;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$toast.error(errorMessage);
        this.blockedNumbers = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },

    async searchNumberAcrossAllPages(searchTerm) {
      try {
        this.loading = true;
        let currentPage = 1;
        let hasMore = true;
        let foundNumber = null;

        while (hasMore && !foundNumber) {
          const params = {
            page: currentPage,
            per_page: 100,
          };

          const response = await this.seller_svc.getBlockedNumbers(params);

          if (!response || !response.data) {
            throw new Error("Resposta inválida da API");
          }

          const numbers = response.data.data.map((item) => ({
            id: item.id,
            phone_number: item.phone,
            blocked_at: item.created_at,
          }));

          foundNumber = numbers.find(
            (item) =>
              this.normalizeForSearch(item.phone_number) ===
              this.normalizeForSearch(searchTerm)
          );

          if (foundNumber) {
            this.blockedNumbers = [foundNumber];
            this.totalItems = 1;
            break;
          }

          currentPage++;
          hasMore = response.data.next_page_url !== null;
        }

        if (!foundNumber) {
          this.blockedNumbers = [];
          this.totalItems = 0;
          this.$toast.info(
            `Número ${this.formatPhoneNumber(this.appliedFilter)} não encontrado`
          );
        }
    } catch (error) {
    let errorMessage = "Erro na busca";
    if (error.message) {
      errorMessage = error.message;
    }
    this.$toast.error(errorMessage);
    this.blockedNumbers = [];
    this.totalItems = 0;
  } finally {
    this.loading = false;
  }
},

    showConfirmationModal(number) {
      this.selectedNumber = number;
      this.showConfirmation = true;
    },
    hideConfirmationModal() {
      this.showConfirmation = false;
      this.selectedNumber = null;
    },
    async confirmRestore() {
      try {
        this.loading = true;
        await this.seller_svc.unblockNumber(this.selectedNumber.phone_number);

        await this.loadBlockedNumbers();

        this.$toast.success("Notificações restauradas com sucesso!");
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            this.$toast.error("Endpoint de desbloqueio não encontrado");
          } else {
            this.$toast.error(
              `Erro ${error.response.status}: ${
                error.response.data.message || "Falha ao restaurar notificações"
              }`
            );
          }
        } else {
          this.$toast.error("Erro de conexão ao tentar restaurar notificações");
        }
      } finally {
        this.loading = false;
        this.hideConfirmationModal();
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    handleSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.$emit("search", this.searchQuery);
      }, 300);
    },
    normalizeForSearch(phone) {
      if (!phone) return "";
      return phone.toString().replace(/\D/g, "");
    },

    applyFilter() {
      this.appliedFilter = this.searchQuery.trim();
      this.currentPage = 1;
      this.loadBlockedNumbers();
    },

    clearFilter() {
      this.searchQuery = "";
      this.appliedFilter = "";
      this.currentPage = 1;
      this.loadBlockedNumbers();
    },

    changePage(page) {
      this.currentPage = page;
      this.loadBlockedNumbers();
    },
  },
  async created() {
    await this.loadBlockedNumbers();
  },
  mounted() {
		window.addEventListener('keydown', this.handlekeydown);
	},
	beforeDestroy(){
		window.removeEventListener('keydown', this.handlekeydown);
	}
};
</script>

<style lang="scss" scoped>
@import "@/sass/commons/_variables";
.search-filter-container {
  display: flex;
  gap: 10px;
  margin-bottom: 1.5em;
  align-items: center;
  width: 50%;
}

.search-input-with-icon {
  position: relative;
  flex-grow: 1;
}

.search-input {
  width: 100%;
  padding: 10px 15px 10px 40px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #777;
  pointer-events: none;
}

.orange-button {
  background-color: #ff6b00;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  white-space: nowrap;
}

.orange-button:hover {
  background-color: var(--primary-color);
}

.clear-button {
  background-color: transparent;
  color: black;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  white-space: nowrap;
  border: 1px solid #b7b4b4;
}

.clear-button:hover {
  background-color: var(--primary-color);
  color: white;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-container {
  width: 80%;
  max-width: 900px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  animation: modal-enter 0.3s ease;
}

.seller-header {
  padding: 1em;
  background: var(--primary-color);
  color: white;
  font-size: 1.2em;
  font-weight: bold;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px 8px 0 0;
}

.seller-header-close {
  cursor: pointer;
}

.blocked-numbers-table-header {
  display: flex;
  padding: 1em;
  font-weight: bold;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  text-align: center;
}

.blocked-numbers-table-row {
  display: flex;
  text-align: center;
  padding: 1em;
  border-bottom: 1px solid #eee;
}

.blocked-number-cell,
.blocked-date-cell,
.blocked-actions-cell {
  flex: 1;
}

.seller-table-edit-icon {
  cursor: pointer;
  color: var(--primary-color);
  margin-right: 10px;
}

.seller-table-edit-icon:hover {
  color: var(--primary-color);
}

.page-table-line-special {
  background-color: #f9f9f9;
}

.no-blocked-numbers {
  padding: 2em;
  text-align: center;
  color: #666;
}

.confirmation-modal-mask {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.confirmation-modal-container {
  width: 400px;
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
}

.confirmation-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.confirmation-modal-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.confirmation-modal-header span {
  cursor: pointer;
  color: #666;
}

.confirmation-modal-body {
  margin-bottom: 20px;
}

.confirmation-modal-body p {
  margin: 0;
  font-size: 1rem;
  color: #555;
}

.confirmation-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-cancel,
.btn-confirm {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;
}

.btn-cancel,
.btn-confirm {
  background-color: #f0f0f0;
  color: #333;
  border: 1px solid #ccc;
}

.btn-cancel:hover,
.btn-confirm:hover {
  background-color: #ff7110;
}

.products-page__datetime-subtitle {
  color: #232323;
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
  }

  .blocked-numbers-table-header {
    display: none;
  }

  .blocked-numbers-table-row {
    flex-direction: column;
    padding: 1em 0.5em;
  }

  .seller-table-header-mobile {
    font-weight: bold;
    margin-bottom: 0.5em;
    color: #666;
  }

  .confirmation-modal-container {
    width: 90%;
  }
}
@media (max-width: 600px) {
  .search-filter-container {
    flex-direction: column;
  }

  .search-input-with-icon {
    width: 100%;
  }

  .orange-button {
    width: 100%;
    padding: 12px;
  }
}
</style>
